import { useEffect, useMemo, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { useConversas } from '@/conversas/ConversasProvider';
import { encaminharMensagem } from '@/services/conversasService';
import { formatarTelefone, normalizarTelefone } from '@/lib/telefone';

/**
 * Para onde encaminhar: uma conversa que já existe ou um número novo.
 *
 * O diálogo manda só os IDS. O texto e o caminho da mídia saem do documento de
 * origem, no servidor — a tela nunca decide o que vai ser enviado, só para onde.
 */

const MENSAGENS_ERRO = {
  'functions/failed-precondition':
    'Não dá para encaminhar esta mensagem por este canal. Confira se o número da recepção está conectado.',
  'functions/permission-denied': 'Seu papel não atende o espaço de origem ou o de destino.',
  'functions/not-found': 'A mensagem ou o arquivo original não está mais disponível.',
  'functions/invalid-argument': 'Escolha uma conversa ou informe um celular válido.',
  'functions/unauthenticated': 'Sua sessão expirou. Entre de novo.',
  // `unavailable` e `deadline-exceeded` NÃO entram aqui: o encaminhar sai pelo
  // mesmo `canais.js` do Composer, e é ele que redige esses textos.
  'functions/internal': 'Não foi possível falar com o servidor. Tente de novo.',
};

const ROTULO_TIPO = {
  imagem: '🖼 Imagem',
  audio: '🎤 Áudio',
  video: '🎬 Vídeo',
  documento: '📄 Documento',
};

/** Resumo do que vai ser encaminhado — a conferência antes de um envio real. */
function Previa({ mensagem }) {
  const rotulo = ROTULO_TIPO[mensagem.tipo];
  return (
    <div className="encaminhar-previa">
      <span className="campo-ajuda">Será encaminhado:</span>
      {rotulo && <strong>{rotulo}</strong>}
      {mensagem.texto ? (
        <p>{mensagem.texto}</p>
      ) : (
        !rotulo && (
          <p>
            <em>(sem texto)</em>
          </p>
        )
      )}
    </div>
  );
}

export default function DialogoEncaminhar({ mensagem, aberto, aoFechar }) {
  const { clinicaId, conversa, conversas } = useConversas();

  const [modo, setModo] = useState('conversa');
  const [busca, setBusca] = useState('');
  const [escolhida, setEscolhida] = useState(null);
  const [telefone, setTelefone] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [feito, setFeito] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setModo('conversa');
    setBusca('');
    setEscolhida(null);
    setTelefone('');
    setErro(null);
    setFeito(null);
  }, [aberto]);

  // A conversa atual sai da lista: encaminhar para ela mesma não faz sentido, e
  // o servidor recusaria depois de a pessoa já ter clicado.
  const candidatas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return conversas
      .filter((c) => c.id !== conversa?.id)
      .filter(
        (c) =>
          !termo ||
          c.contatoNome?.toLowerCase().includes(termo) ||
          c.contatoTelefone?.includes(termo),
      )
      .slice(0, 8);
  }, [conversas, conversa?.id, busca]);

  const podeEnviar =
    !enviando && (modo === 'conversa' ? Boolean(escolhida) : Boolean(telefone.trim()));

  async function enviar() {
    if (!podeEnviar) return;
    setEnviando(true);
    setErro(null);
    try {
      const { data } = await encaminharMensagem({
        clinicaId,
        origemConversaId: conversa.id,
        origemMensagemId: mensagem.id,
        ...(modo === 'conversa'
          ? { destinoConversaId: escolhida.id }
          : { destinoTelefone: normalizarTelefone(telefone) }),
      });
      setFeito({
        nome: modo === 'conversa' ? escolhida.contatoNome : formatarTelefone(normalizarTelefone(telefone)),
        simulada: data.simulada,
      });
    } catch (e) {
      console.error('[OdontoSDR] Falha ao encaminhar:', e);
      setErro(MENSAGENS_ERRO[e?.code] ?? e?.message ?? 'Não foi possível encaminhar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialogo titulo="Encaminhar mensagem" aberto={aberto} aoFechar={aoFechar} largura={520}>
      <div className="dialogo-corpo">
        {feito ? (
          <>
            <p>
              Encaminhada para <strong>{feito.nome}</strong>.
              {feito.simulada && ' (modo simulado — nada saiu para o WhatsApp.)'}
            </p>
            <div className="dialogo-acoes">
              <button type="button" className="btn-primario" onClick={aoFechar}>
                Fechar
              </button>
            </div>
          </>
        ) : (
          <>
            <Previa mensagem={mensagem} />

            <div className="composer-modo">
              <button
                type="button"
                className={modo === 'conversa' ? 'modo ativo' : 'modo'}
                onClick={() => setModo('conversa')}
              >
                Uma conversa
              </button>
              <button
                type="button"
                className={modo === 'telefone' ? 'modo ativo' : 'modo'}
                onClick={() => setModo('telefone')}
              >
                Um número novo
              </button>
            </div>

            {modo === 'conversa' ? (
              <>
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar por nome ou telefone"
                  aria-label="Buscar conversa"
                  disabled={enviando}
                />
                <ul className="encaminhar-lista">
                  {candidatas.length === 0 && (
                    <li className="campo-ajuda">Nenhuma conversa encontrada neste canal.</li>
                  )}
                  {candidatas.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={escolhida?.id === c.id ? 'encaminhar-alvo ativo' : 'encaminhar-alvo'}
                        onClick={() => setEscolhida(c)}
                        disabled={enviando}
                      >
                        <strong>{c.contatoNome || formatarTelefone(c.contatoTelefone)}</strong>
                        <span className="campo-ajuda">{formatarTelefone(c.contatoTelefone)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="campo">
                <label htmlFor="enc-tel">Celular com DDD</label>
                <input
                  id="enc-tel"
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  placeholder="(62) 99999-9999"
                  disabled={enviando}
                />
                {/* Número digitado abre conversa pela linha da recepção — a via
                    não oficial. O número de captação não manda texto livre. */}
                <p className="campo-ajuda">
                  Sai pelo WhatsApp da recepção. Se já houver conversa com este número, a
                  mensagem entra nela.
                </p>
              </div>
            )}

            {erro && <p className="erro">{erro}</p>}

            <div className="dialogo-acoes">
              <button type="button" className="btn-texto" onClick={aoFechar} disabled={enviando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primario"
                onClick={enviar}
                disabled={!podeEnviar}
              >
                {enviando ? 'Encaminhando…' : 'Encaminhar'}
              </button>
            </div>
          </>
        )}
      </div>
    </Dialogo>
  );
}
